import React, { useEffect, useState } from "react";
import { CouponForm, CouponInterface } from "../../interfaces/coupon.interface";
import { useDispatch } from "react-redux";
import { DispatchType } from "../../interfaces/redux.interface";
import { createCoupon, updateCoupon } from "../../redux/slices/CouponSlice";
import { FormControlLabel, Switch } from "@mui/material";
import { errorToast, successToast } from "../../utils/toast.util";

const getErrorMessage = (error: unknown): string => {
  if (typeof error === "string") return error;
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof (error as { message?: string }).message === "string"
  ) {
    return (error as { message: string }).message;
  }
  return "Failed to save coupon. Please try again.";
};

const defaultCouponData: CouponForm = {
  couponCode: "",
  discountType: "percentage",
  discountValue: 0,
  couponFor: "Parent GC Package",
  isPublic: false,
  isActive: true,
  expiryDate: "",
  usageLimit: 1,
};

const CreateCouponModal = ({
  isOpen,
  closeModal,
  isEdit,
  editData,
}: {
  isOpen: boolean;
  closeModal: () => void;
  isEdit?: boolean;
  editData?: CouponInterface;
}) => {
  const dispatch: DispatchType = useDispatch();
  const [couponData, setCouponData] = useState<CouponForm>(defaultCouponData);
  const [isDisabled, setIsDisabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({
    discountValue: "",
    expiryDate: "",
  });

  useEffect(() => {
    if (isEdit && editData) {
      setCouponData({
        couponCode: editData.couponCode,
        discountType: editData.discountType,
        discountValue: editData.discountValue,
        couponFor: editData.couponFor ?? "Parent GC Package",
        isPublic: editData.isPublic ?? false,
        isActive: editData.isActive ?? true,
        expiryDate: editData.expiryDate.split("T")[0],
        usageLimit: editData.usageLimit,
        remainingUses: editData.remainingUses,
      });
    } else {
      setCouponData(defaultCouponData);
    }
  }, [isEdit, editData]);

  useEffect(() => {
    const {
      couponCode,
      discountType,
      discountValue,
      couponFor,
      expiryDate,
      usageLimit,
    } = couponData;

    const isDisabled =
      !couponCode ||
      !discountType ||
      !couponFor ||
      discountValue <= 0 ||
      !expiryDate ||
      usageLimit <= 0 ||
      (expiryDate && new Date(expiryDate) < new Date()) ||
      (discountType === "percentage" && discountValue > 99);

    setIsDisabled(isDisabled);
    setErrors({
      discountValue:
        discountType === "percentage" && discountValue > 99
          ? "Percentage discount cannot exceed 100%"
          : "",
      expiryDate:
        expiryDate && new Date(expiryDate) < new Date()
          ? "Expiry date cannot be in the past"
          : "",
    });
  }, [couponData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setCouponData({
      ...couponData,
      [name]:
        name === "discountValue" || name === "usageLimit"
          ? Number(value)
          : value,
    });
  };

  const handleSwitchChange =
    (field: "isPublic" | "isActive") =>
    (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setCouponData({ ...couponData, [field]: checked });
    };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      if (isEdit && editData) {
        await dispatch(
          updateCoupon({ id: editData._id, data: couponData })
        ).unwrap();
        successToast("Coupon updated successfully");
      } else {
        await dispatch(createCoupon(couponData)).unwrap();
        successToast("Coupon created successfully");
      }
      closeModal();
    } catch (error) {
      errorToast(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white w-full max-w-lg mx-4 p-6 rounded-lg shadow-lg max-h-screen overflow-auto">
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            {isEdit && editData ? "Update" : "Create New"} Coupon
          </h2>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="couponCode" className="block text-left text-gray-700">
              Coupon Code
            </label>
            <input
              type="text"
              name="couponCode"
              value={couponData.couponCode}
              onChange={handleChange}
              placeholder="Coupon Code"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="couponFor" className="block text-left text-gray-700">
              Coupon For
            </label>
            <select
              name="couponFor"
              value={couponData.couponFor}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="Parent GC Package">Parent GC Package</option>
              <option value="Teacher Certification">Teacher Certification</option>
            </select>
          </div>

          <div>
            <label htmlFor="discountType" className="block text-left text-gray-700">
              Discount Type
            </label>
            <select
              name="discountType"
              value={couponData.discountType}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="percentage">Percentage</option>
              <option value="amount">Flat</option>
            </select>
          </div>

          <div>
            <label htmlFor="discountValue" className="block text-left text-gray-700">
              Discount Value
            </label>
            <input
              type="number"
              name="discountValue"
              value={couponData.discountValue}
              onChange={handleChange}
              placeholder="Discount Value"
              className={`w-full px-4 py-2 border ${
                errors.discountValue ? "border-red-500" : "border-gray-300"
              } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            {errors.discountValue && (
              <span className="text-red-500 text-sm text-left pl-1">
                {errors.discountValue}
              </span>
            )}
          </div>

          <div>
            <label htmlFor="expiryDate" className="block text-left text-gray-700">
              Expiry Date
            </label>
            <input
              type="date"
              name="expiryDate"
              value={couponData.expiryDate}
              onChange={handleChange}
              min={new Date().toISOString().split("T")[0]}
              className={`w-full px-4 py-2 border ${
                errors.expiryDate ? "border-red-500" : "border-gray-300"
              } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            {errors.expiryDate && (
              <span className="text-red-500 text-sm text-left pl-1">
                {errors.expiryDate}
              </span>
            )}
          </div>

          <div>
            <label htmlFor="usageLimit" className="block text-left text-gray-700">
              Usage Limit
            </label>
            <input
              type="number"
              name="usageLimit"
              value={couponData.usageLimit}
              onChange={handleChange}
              placeholder="Usage Limit"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <FormControlLabel
              control={
                <Switch
                  checked={couponData.isPublic}
                  onChange={handleSwitchChange("isPublic")}
                  color="warning"
                />
              }
              label="Public Coupon"
            />
            <p className="text-xs text-gray-500 text-left pl-1">
              Only one public coupon is allowed per coupon category
            </p>
          </div>

          <div>
            <FormControlLabel
              control={
                <Switch
                  checked={couponData.isActive}
                  onChange={handleSwitchChange("isActive")}
                  color="success"
                />
              }
              label="Active"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={closeModal}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-white bg-yellow-600 rounded-md hover:bg-yellow-700 transition disabled:opacity-50"
            disabled={isDisabled || isSubmitting}
          >
            {isSubmitting
              ? "Saving..."
              : isEdit && editData
                ? "Update"
                : "Create"}{" "}
            Coupon
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateCouponModal;
